// Usage logging — event batching + server logging via sendBeacon
// Same pattern as Dashboard_Chatbot prototype

const LOG_SERVER_URL = 'https://backend.test.bautvplus.de/process'
const FLUSH_INTERVAL_MS = 10000

let _pendingEvents = []
let _sessionId = null
let _clientId = ''
let _username = ''
let _projectId = ''
let _logPath = ''
let _flushTimer = null

/**
 * Initialize the logger.
 * @param {object} params - { clientId, username, projectId, logPath }
 */
export function init(params) {
    _clientId = params.clientId || ''
    _username = params.username || ''
    _projectId = params.projectId || ''

    // Derive log path from project config
    // Default: use the same pattern as prototype
    _logPath = params.logPath || '/srv/data/sites/usage_log'

    // Generate session ID from timestamp
    _sessionId = _localISO(new Date()).replace(/[:.]/g, '-').substring(0, 19)

    // Start periodic flush
    if (_flushTimer) clearInterval(_flushTimer)
    _flushTimer = setInterval(_flush, FLUSH_INTERVAL_MS)

    // Flush on page unload
    window.addEventListener('beforeunload', _onBeforeUnload)
}

/**
 * Log an event.
 * @param {string} event - event type (e.g. 'chat_message', 'session_start')
 * @param {object} data - additional event data
 */
export function logEvent(event, data = {}) {
    _pendingEvents.push({
        timestamp: _localISO(new Date()),
        event,
        ...data,
    })
}

/**
 * Log a chat message event with full details.
 */
export function logChatMessage(params) {
    logEvent('chat_message', {
        user_message: params.userMessage,
        page: params.currentPage,
        classification: params.classification,
        rag_scope: params.ragScope,
        gatekeeper: params.gatekeeper ? {
            model: params.gatekeeper.model,
            tokens: params.gatekeeper.tokens,
            duration_ms: params.gatekeeper.durationMs,
        } : undefined,
        expensive: params.expensive ? {
            model: params.expensive.model,
            tokens: params.expensive.tokens,
            duration_ms: params.expensive.durationMs,
        } : undefined,
        dashboard_state: params.dashboardState,
        reply: params.reply,
        actions: params.actions,
        total_duration_ms: params.totalDurationMs,
        permission_tier: params.permissionTier,
        language: params.language,
        mode: params.mode,
    })
}

/**
 * Clean up (stop timer, flush remaining events).
 */
export function destroy() {
    if (_flushTimer) {
        clearInterval(_flushTimer)
        _flushTimer = null
    }
    window.removeEventListener('beforeunload', _onBeforeUnload)
    _flush()
}

// ─── Internal ───────────────────────────────────────────────────────────────

function _flush() {
    if (!_pendingEvents.length) return

    const batch = _pendingEvents.splice(0)
    const payload = JSON.stringify({
        scope: 'chatbot_logging',
        client_id: _clientId,
        username: _username,
        log_path: _logPath,
        session_id: _sessionId,
        project_id: _projectId,
        events: batch,
    })

    try {
        navigator.sendBeacon(LOG_SERVER_URL, payload)
    } catch (e) {
        // If sendBeacon fails, try fetch as fallback
        try {
            fetch(LOG_SERVER_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'text/plain' },
                body: payload,
            }).catch(() => {
                // Re-queue events on failure
                _pendingEvents.unshift(...batch)
            })
        } catch (e2) {
            _pendingEvents.unshift(...batch)
        }
    }
}

function _onBeforeUnload() {
    logEvent('session_end')
    _flush()
}

function _localISO(d) {
    const off = d.getTimezoneOffset()
    const local = new Date(d.getTime() - off * 60000)
    const sign = off <= 0 ? '+' : '-'
    const hh = String(Math.floor(Math.abs(off) / 60)).padStart(2, '0')
    const mm = String(Math.abs(off) % 60).padStart(2, '0')
    return local.toISOString().slice(0, -1) + sign + hh + ':' + mm
}
